import React, { useState, useEffect } from 'react';
import {
  Col,
  Row,
  Input,
  ModalHeader,
  ModalBody,
  Label,
  ModalFooter,
  Modal,
  Form,
  FormFeedback,
} from 'reactstrap';
import { useForm, Controller, FieldErrors, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { isVisible } from 'slices/loader/reducer';
import { removeFile, uploadFile } from 'services/fileService';
import {
  addNewRegistration,
  editNewRegistration,
} from 'services/registrations';
import { FilePreview } from 'Components/Common/FilePreview';
import { uploadFieldCategoryMap } from 'Components/constants/uploadFieldCategoryMap';

type UploadedFile = {
  url: string;
  uploadedAt: number;
};

type UploadField =
  | 'addressProof'
  | 'PTRDoc'
  | 'directivesPhoto'
  | 'trustDeed'
  | 'trustRegistrationCertificate'
  | 'changeReports'
  | 'charityPermissions'
  | 'panCardOfTrust'
  | 'registrationCertificates12AA80G'
  | 'resolutionRegisterMinutesBook'
  | 'bankAccountDetails'
  | 'waqfDeed'
  | 'waqfRegistrationCertificate'
  | 'changeOrder'
  | 'intimationToWaqfBoard'
  | 'sanctionOrders'
  | 'waqfBoardAuditReports'
  | 'waqfPropertyListAsPerAct';

interface CreateRegistrationModelProps {
  isOpen: boolean;
  toggle: () => void;
  type: string;
  organizationId: number;
  organizationName: string;
  onSuccess: () => void;
  editData?: any;
}

type RegistrationFormValues = {
  trustRegNumber: string;
  purposeOfTrust: string;
  soleTrustee: string | null;
  managingTrusteeCommittee: string | null;
};

const CreateRegistrationModel = ({
  isOpen,
  toggle,
  type,
  organizationId,
  organizationName,
  onSuccess,
  editData,
}: CreateRegistrationModelProps) => {
  const dispatch = useDispatch();
  const [uploads, setUploads] = useState<Record<UploadField, UploadedFile[]>>({
    addressProof: [],
    PTRDoc: [],
    directivesPhoto: [],
    trustDeed: [],
    trustRegistrationCertificate: [],
    changeReports: [],
    charityPermissions: [],
    panCardOfTrust: [],
    registrationCertificates12AA80G: [],
    resolutionRegisterMinutesBook: [],
    bankAccountDetails: [],
    waqfDeed: [],
    waqfRegistrationCertificate: [],
    changeOrder: [],
    intimationToWaqfBoard: [],
    sanctionOrders: [],
    waqfBoardAuditReports: [],
    waqfPropertyListAsPerAct: [],
  });

  const validationSchema = Yup.object().shape({
    trustRegNumber: Yup.string()
      .transform(v => (typeof v === 'string' ? v.trim() : v))
      .required('Enter registration number'),
    purposeOfTrust: Yup.string()
      .transform(v => (typeof v === 'string' ? v.trim() : v))
      .required('Enter purpose of trust'),
    soleTrustee: Yup.string().nullable(),
    managingTrusteeCommittee: Yup.string().nullable(),
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<RegistrationFormValues>({
    resolver: yupResolver(validationSchema, {
      abortEarly: false,
    }) as Resolver<RegistrationFormValues>,
    defaultValues: {
      trustRegNumber: editData?.trustRegNumber || '',
      purposeOfTrust: editData?.purposeOfTrust || '',
      soleTrustee: editData?.soleTrustee || null,
      managingTrusteeCommittee: editData?.managingTrusteeCommittee || null,
    },
  });

  const handleMultiUpload = async (
    files: FileList | null,
    field: UploadField,
  ) => {
    if (!files || !files.length) return;

    try {
      dispatch(isVisible(true));
      const fileArr = Array.from(files);
      const results = await Promise.all(
        fileArr.map(file =>
          uploadFile({
            file,

            category: uploadFieldCategoryMap[field],

            customFileName: `${field}-${Date.now()}`,

            organizationName,

            trustRegNumber: watch('trustRegNumber'),
          }),
        ),
      );

      const newFiles: UploadedFile[] = results.map(r => ({
        url: r.data.url,
        uploadedAt: Date.now(),
      }));

      setUploads(prev => {
        const updated = [...newFiles, ...prev[field]].sort(
          (a, b) => b.uploadedAt - a.uploadedAt,
        );
        setValue(field as any, updated.map(f => f.url).join(','));
        return { ...prev, [field]: updated };
      });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Upload failed');
    } finally {
      dispatch(isVisible(false));
    }
  };

  const removeUploadedFile = async (field: UploadField, index: number) => {
    const file = uploads[field][index];
    dispatch(isVisible(true));

    setUploads(prev => {
      const updated = prev[field].filter((_, i) => i !== index);
      setValue(field as any, updated.map(f => f.url).join(','));
      return { ...prev, [field]: updated };
    });

    if (file?.url) {
      try {
        await removeFile([file.url]);
      } catch (error) {
        toast.error(`Error fetching Mazar: ${error}`, {
          autoClose: 2000,
        });
      } finally {
        dispatch(isVisible(false));
      }
    }
  };

  useEffect(() => {
    if (editData) {
      reset({
        trustRegNumber: editData?.trustRegNumber || '',
        purposeOfTrust: editData?.purposeOfTrust || '',
        soleTrustee: editData?.soleTrustee || null,
        managingTrusteeCommittee: editData?.managingTrusteeCommittee || null,
      });

      const fields: UploadField[] = [
        'addressProof',
        'PTRDoc',
        'directivesPhoto',
        'trustDeed',
        'trustRegistrationCertificate',
        'changeReports',
        'charityPermissions',
        'panCardOfTrust',
        'registrationCertificates12AA80G',
        'resolutionRegisterMinutesBook',
        'bankAccountDetails',
        'waqfDeed',
        'waqfRegistrationCertificate',
        'changeOrder',
        'intimationToWaqfBoard',
        'sanctionOrders',
        'waqfBoardAuditReports',
        'waqfPropertyListAsPerAct',
      ];

      const next: Record<UploadField, UploadedFile[]> = { ...uploads };
      fields.forEach(f => {
        const raw = editData?.[f];
        const urls = raw ? String(raw).split(',').filter(Boolean) : [];
        next[f] = urls.map(url => ({ url, uploadedAt: Date.now() }));
      });
      setUploads(next);
    } else {
      setUploads({
        addressProof: [],
        PTRDoc: [],
        directivesPhoto: [],
        trustDeed: [],
        trustRegistrationCertificate: [],
        changeReports: [],
        charityPermissions: [],
        panCardOfTrust: [],
        registrationCertificates12AA80G: [],
        resolutionRegisterMinutesBook: [],
        bankAccountDetails: [],
        waqfDeed: [],
        waqfRegistrationCertificate: [],
        changeOrder: [],
        intimationToWaqfBoard: [],
        sanctionOrders: [],
        waqfBoardAuditReports: [],
        waqfPropertyListAsPerAct: [],
      });
    }
  }, [editData, isOpen, reset]);

  const onSubmitInvalid = (errs: FieldErrors<RegistrationFormValues>) => {
    const parts = [errs.trustRegNumber?.message, errs.purposeOfTrust?.message]
      .filter(Boolean)
      .join(' ');
    toast.error(
      parts || 'Please fill Trust Registration Number and Purpose of Trust.',
    );
  };

  const onSubmit = async (values: RegistrationFormValues) => {
    try {
      const joinOrNull = (field: UploadField) => {
        const s = uploads[field]?.map(f => f.url).join(',') || '';
        return s.length ? s : null;
      };
      const registrationData: any = {
        ...values,
        organizationId: organizationId,
        category: type,
        addressProof: joinOrNull('addressProof'),
        PTRDoc: joinOrNull('PTRDoc'),
        directivesPhoto: joinOrNull('directivesPhoto'),
        trustDeed: joinOrNull('trustDeed'),
        trustRegistrationCertificate: joinOrNull(
          'trustRegistrationCertificate',
        ),
        changeReports: joinOrNull('changeReports'),
        charityPermissions: joinOrNull('charityPermissions'),
        panCardOfTrust: joinOrNull('panCardOfTrust'),
        registrationCertificates12AA80G: joinOrNull(
          'registrationCertificates12AA80G',
        ),
        resolutionRegisterMinutesBook: joinOrNull(
          'resolutionRegisterMinutesBook',
        ),
        bankAccountDetails: joinOrNull('bankAccountDetails'),
        waqfDeed: joinOrNull('waqfDeed'),
        waqfRegistrationCertificate: joinOrNull('waqfRegistrationCertificate'),
        changeOrder: joinOrNull('changeOrder'),
        intimationToWaqfBoard: joinOrNull('intimationToWaqfBoard'),
        sanctionOrders: joinOrNull('sanctionOrders'),
        waqfBoardAuditReports: joinOrNull('waqfBoardAuditReports'),
        waqfPropertyListAsPerAct: joinOrNull('waqfPropertyListAsPerAct'),
      };
      if (editData) {
        await editNewRegistration(editData.id, registrationData);
      } else {
        await addNewRegistration(registrationData);
      }
      toast.success(editData ? 'Registration updated' : 'Registration added');
      onSuccess();
      toggle();
      reset();
      setUploads({
        addressProof: [],
        PTRDoc: [],
        directivesPhoto: [],
        trustDeed: [],
        trustRegistrationCertificate: [],
        changeReports: [],
        charityPermissions: [],
        panCardOfTrust: [],
        registrationCertificates12AA80G: [],
        resolutionRegisterMinutesBook: [],
        bankAccountDetails: [],
        waqfDeed: [],
        waqfRegistrationCertificate: [],
        changeOrder: [],
        intimationToWaqfBoard: [],
        sanctionOrders: [],
        waqfBoardAuditReports: [],
        waqfPropertyListAsPerAct: [],
      });
    } catch (e: any) {
      toast.error(`Error saving registration: ${e}`);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" backdrop="static" centered>
      <ModalHeader toggle={toggle} className="bg-light p-3">
        {editData ? 'Edit Registration' : 'Add Registration'}
      </ModalHeader>
      <Form onSubmit={handleSubmit(onSubmit, onSubmitInvalid)}>
        <ModalBody className="overflow-hidden">
          <Row className="g-3">
            <Col lg={12}>
              <Label className="form-label">Category Type</Label>
              <Input type="text" value={type} disabled readOnly />
            </Col>
            <Col lg={6}>
              <Label className="form-label">Trust Registration Number *</Label>
              <Controller
                name="trustRegNumber"
                control={control}
                render={({ field }) => (
                  <Input {...field} invalid={!!errors.trustRegNumber} />
                )}
              />
              {errors.trustRegNumber && (
                <FormFeedback>{errors.trustRegNumber.message}</FormFeedback>
              )}
            </Col>
            <Col lg={6}>
              <Label className="form-label">Purpose of Trust *</Label>
              <Controller
                name="purposeOfTrust"
                control={control}
                render={({ field }) => (
                  <Input {...field} invalid={!!errors.purposeOfTrust} />
                )}
              />
              {errors.purposeOfTrust && (
                <FormFeedback>{errors.purposeOfTrust.message}</FormFeedback>
              )}
            </Col>
            <Col lg={6}>
              <Label className="form-label">Sole Trustee</Label>
              <Controller
                name="soleTrustee"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.soleTrustee && (
                <FormFeedback>{errors.soleTrustee.message}</FormFeedback>
              )}
            </Col>
            <Col lg={6}>
              <Label className="form-label">Managing Trustee Committee</Label>
              <Controller
                name="managingTrusteeCommittee"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.managingTrusteeCommittee && (
                <FormFeedback>
                  {errors.managingTrusteeCommittee.message}
                </FormFeedback>
              )}
            </Col>
            <Col lg={6}>
              <Label className="form-label">Address Proof (Image/PDF)</Label>
              <Input
                type="file"
                multiple
                onChange={e =>
                  handleMultiUpload(e.target.files, 'addressProof')
                }
              />
              <FilePreview
                files={uploads.addressProof}
                onRemove={i => removeUploadedFile('addressProof', i)}
              />
            </Col>
            <Col lg={6}>
              <Label className="form-label">PTR Document (Image/PDF)</Label>
              <Input
                type="file"
                multiple
                onChange={e => handleMultiUpload(e.target.files, 'PTRDoc')}
              />
              <FilePreview
                files={uploads.PTRDoc}
                onRemove={i => removeUploadedFile('PTRDoc', i)}
              />
            </Col>
            <Col lg={12}>
              <Label className="form-label">
                Photo of Directives (Image/PDF)
              </Label>
              <Input
                type="file"
                multiple
                onChange={e =>
                  handleMultiUpload(e.target.files, 'directivesPhoto')
                }
              />
              <FilePreview
                files={uploads.directivesPhoto}
                onRemove={i => removeUploadedFile('directivesPhoto', i)}
              />
            </Col>

            <hr className="my-2" />
            <h6>Trust / Waqf Documents</h6>

            {[
              {
                label: 'Trust Deed',
                field: 'trustDeed' as UploadField,
              },
              {
                label: 'Trust Registration Certificate',
                field: 'trustRegistrationCertificate' as UploadField,
              },
              {
                label: 'Change Reports',
                field: 'changeReports' as UploadField,
              },
              {
                label: 'Charity Permissions',
                field: 'charityPermissions' as UploadField,
              },
              {
                label: 'PAN Card of Trust',
                field: 'panCardOfTrust' as UploadField,
              },
              {
                label: '12AA/80G Registration Certificates',
                field: 'registrationCertificates12AA80G' as UploadField,
              },
              {
                label: 'Resolution Register / Minutes Book',
                field: 'resolutionRegisterMinutesBook' as UploadField,
              },
              {
                label: 'Bank Account Details',
                field: 'bankAccountDetails' as UploadField,
              },
              {
                label: 'Waqf Deed',
                field: 'waqfDeed' as UploadField,
              },
              {
                label: 'Waqf Registration Certificate',
                field: 'waqfRegistrationCertificate' as UploadField,
              },
              {
                label: 'Change Order',
                field: 'changeOrder' as UploadField,
              },
              {
                label: 'Intimation to Waqf Board (property change/lease)',
                field: 'intimationToWaqfBoard' as UploadField,
              },
              {
                label: 'Sanction Orders (major works/transactions)',
                field: 'sanctionOrders' as UploadField,
              },
              {
                label: 'Waqf Board Audit Reports',
                field: 'waqfBoardAuditReports' as UploadField,
              },
              {
                label: 'Waqf Property List as per Act',
                field: 'waqfPropertyListAsPerAct' as UploadField,
              },
            ].map(item => (
              <Col lg={6} key={item.field}>
                <Label className="form-label">{item.label}</Label>
                <Input
                  type="file"
                  multiple
                  onChange={e => handleMultiUpload(e.target.files, item.field)}
                />
                <FilePreview
                  files={uploads[item.field]}
                  onRemove={i => removeUploadedFile(item.field, i)}
                />
              </Col>
            ))}
          </Row>
        </ModalBody>
        <ModalFooter>
          <button className="btn btn-light" type="button" onClick={toggle}>
            Close
          </button>
          <button className="btn btn-success" type="submit">
            {editData ? 'Update' : 'Create'}
          </button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CreateRegistrationModel;
